// src/shared/navigation/SideMenu/components/WhatsNewCard.tsx
import React from "react";
import { ReactComponent as IconChangelog } from "@assets/Icons/IconChangelog.svg";
import { useOpenWhatsNew } from "@shared/changelog/WhatsNewBoundary";
import { useUnseenRelease } from "@shared/changelog/useUnseenRelease";
import { formatReleaseDate } from "@shared/changelog/format";
import {
  FOCUS_RING,
  LABEL_CLAMP,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_TEXT,
  SUBLINE_TEXT,
} from "../sideMenuDensity";

export type WhatsNewCardProps = {
  /** Expanded rail or mobile drawer — the card layout needs that width. */
  showWide: boolean;
  /** The footer's shared row class, for the collapsed icon-only fallback. */
  rowClass: string;
  /** The footer's icon size for the current rail width. */
  rowIcon: string;
};

/**
 * "See what's new" — the changelog's entry point in the SideMenu footer
 * (CTX-224), and the trigger for the What's new walkthrough.
 *
 * It replaces a chip that navigated to `/changelog`. The chip was the same
 * weight as `Technical Support` beside it, so a release nobody had read looked
 * exactly like a link to documentation; the card announces instead of filing.
 *
 * Three states, and only the first two are ours to name: **default** on the
 * rail's own surface, **unseen** tinted with the brand tokens — which is what
 * replaces the old `●` dot in the expanded rail — and **pressed**, left to
 * `active:` so no state variable tracks something the browser already knows.
 *
 * Renders nothing when the feed is empty or unreachable: `useUnseenRelease`
 * reports an empty `releasedAt` for both, and a card that opens an empty dialog
 * is worse than no card. The production gate lives at the call site, so the
 * feed is not even fetched where the changelog is hidden.
 */
const WhatsNewCard: React.FC<WhatsNewCardProps> = ({
  showWide,
  rowClass,
  rowIcon,
}) => {
  const { releasedAt, hasUnseen } = useUnseenRelease();

  /**
   * Opening is not reading (CTX-591): the card used to mark the release seen
   * before the dialog even mounted, so a `✕` on `1 of 3` cleared the tint for
   * two entries nobody had been shown. The modal marks what it actually showed.
   *
   * And the dialog is no longer this component's to hold (CTX-592). The rail
   * renders a **different tree** while the profile loads (`SideMenuBody` swaps
   * in `SideMenuSkeleton`), so everything below it — this card included — is
   * unmounted and rebuilt the moment the profile lands. A dialog whose open
   * state lived here died with that swap, which is precisely why the tour's
   * state lives at `AppLayout` too.
   */
  const open = useOpenWhatsNew();

  if (!releasedAt) return null;

  const unseenSurface = hasUnseen
    ? "border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary"
    : "border-semantic-border-secondary bg-semantic-bg-primary";

  const trigger = showWide ? (
    <button
      type="button"
      onClick={open}
      title="See what's new"
      aria-label={`See what's new${hasUnseen ? ", new updates available" : ""}`}
      className={`
        group flex w-full flex-col border
        ${ROW_PADDING}
        ${ROW_RADIUS}
        ${unseenSurface}
        transition-colors duration-200 motion-reduce:transition-none
        hover:border-semantic-border-primary hover:bg-semantic-bg-secondary
        active:border-semantic-border-primary active:bg-semantic-bg-tertiary
        ${FOCUS_RING}
      `}
    >
      <span className="flex w-full items-center gap-2">
        <span
          className={`flex-1 text-left ${ROW_TEXT} ${LABEL_CLAMP} font-medium text-semantic-text-brand`}
        >
          See what&apos;s new
        </span>
        {/* Diagonal expand, not a chevron or an external-link glyph: it has to
            read as "opens a dialog" rather than "navigates" or "leaves the
            app" — the misreading the chip it replaces invited. */}
        <svg
          className="h-3 w-3 flex-shrink-0 text-semantic-text-brand"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 3h6v6M21 3l-7 7M9 21H3v-6M3 21l7-7"
          />
        </svg>
      </span>
      {/* `text-left` is load-bearing, not decoration: a `<button>` centres its
          text by default, so without it the date sits centred under a
          left-aligned label — which is what it did until QA caught it. The
          primary line above carries the same class for the same reason. */}
      <span className={`text-left ${SUBLINE_TEXT} text-semantic-text-secondary`}>
        {formatReleaseDate(releasedAt)}
      </span>
    </button>
  ) : (
    /* Collapsed rail: the icon-only row every other footer control uses, with
       the unseen mark back as a dot — the card's two lines have nowhere to go at
       64px, and `sideMenuDensity` already establishes that a rail badge becomes
       a dot rather than a shrunken pill.

       The dot keeps the footer's own measured geometry (4px of fill in a 1px
       ring) rather than `BADGE_DOT`'s 6px: that constant is sized against a nav
       row's glyph, and the chip this replaces recorded that 6px + a 2px ring
       measured the full icon height here. */
    <button
      type="button"
      onClick={open}
      title="See what's new"
      aria-label={`See what's new${hasUnseen ? ", new updates available" : ""}`}
      className={`relative ${rowClass}`}
    >
      <span className="relative flex-shrink-0">
        <IconChangelog className={`${rowIcon} text-semantic-text-secondary`} />
        {hasUnseen && (
          <span
            className="absolute -right-0.5 -top-0.5 h-1 w-1 rounded-full bg-semantic-bg-brand-bold ring-1 ring-semantic-bg-primary"
            aria-hidden
          />
        )}
      </span>
    </button>
  );

  return trigger;
};

export default WhatsNewCard;
